import {
  Component,
  Inject,
  Input,
  OnInit,
  QueryList,
  ViewChildren
} from '@angular/core';

import { DynamicFormService } from '@ntap/common';
import {
  ActionComponent,
  BackendDataSourceService,
  BusinessItem,
  CoreConfig,
  CoreConfigService,
  DynamicComponent,
  InputComponentUtil,
  InputControlData,
  InputUtils,
  Item,
  ItemView,
  ItemViewProviderDirective,
  NtapGlobalConstants,
  Util,
  View
} from '@ntap/core';
import {
  PrimeNGListActionBarComponent,
  PrimeNGTableComponent,
  TableData
} from '@ntap/primeng';

import { FormControl, FormGroup } from '@angular/forms';
import { RefreshableAttributeStateService } from '@app/services/refreshable-attribute-state.service';
import { map, switchMap, tap } from 'rxjs';
import { TableMetaWithDataKey } from '../editable-table.component/editable-table.component';
import { InMemoryListViewProviderDirective } from '../editableitemlist.component/services/in-memory-list-view-provider.directive';
import { ARHIV_POSJEDNIK } from '@app/shared/models/types';

@Component({
  selector: '[app-editable-table-row]', // eslint-disable-line @angular-eslint/component-selector
  templateUrl: './editable-table-row.component.html',
  styleUrls: ['./editable-table-row.component.css'],
  providers: [
    DynamicFormService
    // { provide: FORM_SERVICE_PROVIDER_TOKEN, useExisting: DynamicFormService }
  ]
})
export class EditableTableRowComponent
  extends PrimeNGTableComponent
  implements OnInit
{
  @Input()
  public parentItem: any;
  @Input()
  public relToParentItemKey = 'parent_id';

  public formInvalid = true;

  @Input() item: Item;
  @Input() tableMeta: TableMetaWithDataKey;
  @Input() tableData: TableData;
  @Input() tableItem: Item;
  @Input() editing: boolean;
  @Input() rowIndex: number;
  @Input() readOnly = false;

  public inputs: InputControlData[];

  clonedItems: { [s: string]: Item } = {};

  private excludedColumnNames: string[] = [
    'rel~jedinica_gradiva_veze~naziv',
    'rel~jedinica_gradiva_veze~url',
    'rel~jedinica_gradiva_veze~povezana_jedinica_gradiva_id',
    'en~agent~naziv',
    'seup~subjekt~naziv',
    'sv~autor~identifikator',
    'en~mjesto~glavni_naziv'
  ];

  constructor(
    protected override listViewProvider: InMemoryListViewProviderDirective,
    @Inject(CoreConfigService) protected override coreConfig: CoreConfig,
    protected override attributeStateService: RefreshableAttributeStateService,
    protected backendDataSource: BackendDataSourceService,
    protected dynamicFormService: DynamicFormService,
    protected itemViewProvider: ItemViewProviderDirective<ItemView>
  ) {
    super(listViewProvider, coreConfig, attributeStateService);
  }

  readonly OSTALI_NAZIVI_MASTER_ID = 'sv~naziv~entitet_id';
  readonly AGENT_ID = 'en~agent~id';

  ngOnInit() {
    this.inputs = this.extractInputs(this.tableMeta.view, this.item);
    this.addSubscription(
      this.dynamicFormService.formGroup$
        .pipe(
          tap((fg) => {
            this.addRowRequiredValidators(fg);
          }),
          switchMap((fg) => {
            return fg.statusChanges.pipe(
              map((status) => {
                this.formInvalid = status !== 'VALID';
              })
            );
          })
        )
        .subscribe()
    );
  }

  private addRowRequiredValidators(fg: FormGroup) {
    fg.addValidators([
      (value: any) => {
        const anyValuePresent = Object.keys(value.controls)
          .map((controlName) => value.controls[controlName])
          .some((formControl: FormControl) => {
            return (
              (typeof formControl.value === 'string' &&
                formControl.value.length > 0) ||
              (formControl.value !== undefined && formControl.value !== null)
            );
          });
        return anyValuePresent ? null : { noValuesEntered: '' };
      }
    ]);
  }

  public extractInputs(viewMeta: View, item: any) {
    const inputs = InputUtils.attrsToInputControls(
      viewMeta.attributes,
      (inputControl: InputControlData) =>
        item[inputControl.attributeMetadata.name],
      () => item,
      () => this.listViewProvider.viewName
    );
    this.initializeControls(inputs);
    return inputs;
  }

  initializeControls(inputControlParams: InputControlData[]) {
    this.dynamicFormService.reInitializeFormGroup();
    inputControlParams.forEach((param) => {
      const validators = InputComponentUtil.getValidators(param);
      this.dynamicFormService.registerFormControl(
        param.attributeMetadata.name,
        { value: param.itemValue, disabled: param.disabled },
        validators
      );
    });
  }

  onRowEditInit(tableMeta: TableMetaWithDataKey, item: any) {
    const itemId = item[tableMeta.dataKey];
    this.clonedItems[itemId] = { ...item };
  }

  createBusinessItem(item: any) {
    // FIXME ovo ne bi trebalo biti potrebno, business item bi sam trebao znati postaviti $mainType.
    const itemMainType = item['$mainType'] || item['$itemType'];
    const itemWithMainType = Object.assign(
      { $mainType: itemMainType },
      this.toMainItemData(this.inputs[0].objectData)
    );
    if (!!this.relToParentItemKey && this.parentItem) {
      const relationToParentAttrName = Util.getFullAttrName(
        itemMainType,
        this.relToParentItemKey
      );

      let parentId: string;
      // FIXME vidjeti ako ovo treba
      // ovo je fix, jer nemamo nasljedivanje nego kompoziciju/kombinaciju podataka 2 tipa, a embedded view ne zna raditi s parentom koji nije $mainType
      if (
        relationToParentAttrName == this.OSTALI_NAZIVI_MASTER_ID &&
        itemMainType === ARHIV_POSJEDNIK
      ) {
        parentId = this.parentItem[this.AGENT_ID];
      } else {
        parentId =
          this.parentItem[
            Util.getFullAttrName(
              this.parentItem['$mainType'],
              NtapGlobalConstants.ATTR_id
            )
          ];
      }
      if (itemWithMainType['$itemType'] !== 'seup~osoba') {
        itemWithMainType[relationToParentAttrName] = parentId;
      }
    }
    const businessItem = new BusinessItem(itemWithMainType);
    return businessItem;
  }

  onRowEditSave(tableMeta: TableMetaWithDataKey, item: any, rowIndex: number) {
    this.inputs.forEach((param) => {
      item[param.attributeMetadata.name] =
        this.dynamicFormService.getFormValues()[param.attributeMetadata.name];
    });

    delete this.clonedItems[item[tableMeta.dataKey]];

    // TODO provjeriti je li ovo potrebno na zadnjem NTAP-u,
    // mozda se sve moze rijesit slanjem $parentId i $parentType
    const onlyItemMainTypeAttrs: any = this.toMainItemData(item);

    const businessItem = this.createBusinessItem(onlyItemMainTypeAttrs);
    this.backendDataSource
      .saveViewData('$defaultItemView', businessItem)
      .pipe(
        tap((savedItem: any) => {
          const updatedItem = Object.assign({}, businessItem.data, savedItem);
          // have to update the item too, the change from listViewProvider is not propagated for some reason
          item = updatedItem;
          this.listViewProvider.updateItem(updatedItem, rowIndex);
        })
      )
      .subscribe();
  }

  private toMainItemData(item: any) {
    const itemMainType = item['$mainType'] || item['$itemType'];

    const onlyItemMainTypeAttrs: any = {};

    Object.keys(item)
      .filter((key) => key.startsWith(itemMainType))
      .forEach((key) => {
        onlyItemMainTypeAttrs[key] = item[key];
      });

    onlyItemMainTypeAttrs['$itemType'] = item['$itemType'];
    onlyItemMainTypeAttrs['$mainType'] = item['$mainType'];
    return onlyItemMainTypeAttrs;
  }

  onRowEditCancel(
    tableMeta: TableMetaWithDataKey,
    item: any,
    rowIndex: number
  ) {
    const itemId = item[tableMeta.dataKey];
    const itemType = item['$itemType'];
    const idAttr = Util.getFullAttrName(itemType, NtapGlobalConstants.ATTR_id);
    if (itemType && item[idAttr] <= 0) {
      const businessItem = new BusinessItem(this.toMainItemData(item));
      this.listViewProvider
        .deleteItemAtIndex(businessItem, rowIndex)
        .subscribe();
      delete this.clonedItems[itemId];
    } else {
      this.listViewProvider.cancelUpdateItem(item);
    }
  }

  onRowDelete(tableMeta: TableMetaWithDataKey, item: any, rowIndex: number) {
    const itemId = item[tableMeta.dataKey];
    delete this.clonedItems[itemId];
    //  this.table.cancelRowEdit(item);
    const businessItem = new BusinessItem(this.toMainItemData(item));
    const tableRows = this.tableData.rows || 1;
    const itemIndex = tableMeta.pagingSetup.lazy
      ? rowIndex - Math.floor(rowIndex / tableRows) * tableRows
      : rowIndex;
    this.listViewProvider
      .deleteItemAtIndex(businessItem, itemIndex)
      .subscribe();
  }

  @ViewChildren('rowActionBar')
  dynamicActionToolbars: QueryList<DynamicComponent>;

  isOpenItemColumn(colName: string, view: View) {
    return (
      (colName.indexOf('povezana') ||
        colName.indexOf('sv~autor~identifikator')) >= 0 &&
      view.actions.filter((a: any) => a.name.indexOf('open') >= 0).length > 0
    );
  }

  // TODO refaktorirati, sad imamo isti kod i u tablelistgrid
  openItem(event: Event) {
    event.preventDefault();
    for (const dynamicActionToolbar of this.dynamicActionToolbars) {
      if (dynamicActionToolbar) {
        const primengActionBar =
          dynamicActionToolbar.getViewComponent() as PrimeNGListActionBarComponent;
        const dynamicActionComp = primengActionBar.dynamicComponents.find(
          (c) =>
            (c.getViewComponent() as ActionComponent).action.name.indexOf(
              'open'
            ) >= 0
        );
        if (dynamicActionComp) {
          const actionComp = dynamicActionComp.getViewComponent() as any;
          actionComp.execute();
          break;
        }
      }
    }
  }

  isColumnExcluded(columnName: string): boolean {
    return this.excludedColumnNames.includes(columnName);
  }
}
